import { useId } from 'react';

import { pathLabel, pathRoleOf } from '@/shared/program/path-label';

import type { ModulePlacement, PlacementRecommendation } from '@mio/shared';
import type { KeyboardEvent } from 'react';

export interface PlacementModuleView {
  number: number;
  title: string;
  placement: ModulePlacement | null;
}

export type ModuleChoice = 'start' | 'include' | 'skip';

const CHOICES: { value: ModuleChoice; label: string }[] = [
  { value: 'start', label: 'Start here' },
  { value: 'include', label: 'Include' },
  { value: 'skip', label: 'Skip' },
];

function choiceOf(module: number, startModule: number, chosenModules: number[]): ModuleChoice {
  if (startModule === module) return 'start';
  return chosenModules.includes(module) ? 'include' : 'skip';
}

/** One three-way control per module; arrow keys move between the choices. */
function ChoiceControl({
  module,
  value,
  onChoose,
}: {
  module: PlacementModuleView;
  value: ModuleChoice;
  onChoose: (choice: ModuleChoice) => void;
}) {
  const id = useId();
  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? CHOICES.length - 1
          : (index + (event.key === 'ArrowRight' ? 1 : CHOICES.length - 1)) % CHOICES.length;
    const choice = CHOICES[next];
    if (!choice) return;
    onChoose(choice.value);
    document.getElementById(`${id}-${choice.value}`)?.focus();
  }
  return (
    <div
      role="radiogroup"
      aria-label={`Module ${module.number}: ${module.title}`}
      className="path-segment"
    >
      {CHOICES.map((choice, index) => (
        <button
          key={choice.value}
          id={`${id}-${choice.value}`}
          type="button"
          role="radio"
          aria-checked={value === choice.value}
          tabIndex={value === choice.value ? 0 : -1}
          onClick={() => onChoose(choice.value)}
          onKeyDown={(event) => onKeyDown(event, index)}
        >
          {choice.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The module list with the learner's choice for each one. Every module stays openable
 * whatever is chosen here; the choice only sets labels and the starting point.
 */
export function PlacementResult({
  modules,
  recommendation,
  chosenModules,
  startModule,
  onChoose,
  compact = false,
}: {
  modules: PlacementModuleView[];
  recommendation: PlacementRecommendation;
  chosenModules: number[];
  startModule: number;
  onChoose: (module: number, choice: ModuleChoice) => void;
  /** Single-column layout for the dialog. */
  compact?: boolean;
}) {
  const path = { startModule, modules: chosenModules, source: 'adjusted' as const };
  return (
    <ol className="path-modules" data-compact={compact} aria-label="Modules">
      {modules.map((module) => {
        const reason = recommendation.rationale.find((item) => item.module === module.number);
        const role = pathRoleOf(path, module.number);
        const choice = choiceOf(module.number, startModule, chosenModules);
        return (
          <li key={module.number} className="path-module" data-role={role}>
            <div>
              <div className="path-module-title">
                <span className="path-module-number">Module {module.number}</span>
                <span>{module.title}</span>
                {role ? (
                  <span className="path-tag" data-tone={role}>
                    {pathLabel(role)}
                  </span>
                ) : null}
                {reason?.recommended ? (
                  <span className="path-tag" data-tone="recommended">
                    Recommended
                  </span>
                ) : null}
              </div>
            </div>
            <ChoiceControl
              module={module}
              value={choice}
              onChoose={(next) => onChoose(module.number, next)}
            />
            {reason ? <p className="path-module-reason">{reason.reason}</p> : null}
            {module.placement ? (
              <details>
                <summary>Who it is for and what it assumes</summary>
                <p>{module.placement.audience}</p>
                {module.placement.prerequisites.length > 0 ? (
                  <ul>
                    {module.placement.prerequisites.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : null}
                <p>{module.placement.skipWhen}</p>
              </details>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
